/**
 * Language provider + `useT()` for the English | Hinglish toggle.
 *
 * Behaviour contract:
 *  - `initial` comes from the root route, resolved from the cookie on the
 *    server, so returning Hinglish users get Hinglish in the SSR HTML (no
 *    flash of English).
 *  - Switching is pure React state: instant, no reload, so scroll position,
 *    cart contents, selected packs, form values and quiz progress survive.
 *  - Missing Hinglish keys fall back to the complete English string. Keys are
 *    recorded on `window.__truweMissingI18n` for the coverage report; nothing
 *    is ever rendered as a raw key or a blank.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  clearSavedLang,
  DEFAULT_LANG,
  HINGLISH_ENABLED,
  LANG_LABELS,
  persistLang,
  readSavedLang,
  type Lang,
} from "./config";
import { en, type TranslationKey } from "./dictionary";
import { pushContentLanguage, trackLanguageToggle } from "./analytics";

type Vars = Record<string, string | number>;

interface LanguageContextValue {
  lang: Lang;
  /** Human label of the active language, e.g. "Hinglish". */
  langLabel: string;
  setLang: (next: Lang) => void;
  t: (key: TranslationKey, vars?: Vars) => string;
  /** True when the string for this key exists in the active language. */
  has: (key: TranslationKey) => boolean;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

function interpolate(template: string, vars?: Vars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in vars ? String(vars[name]) : whole,
  );
}

function recordMissing(key: TranslationKey): void {
  if (typeof window === "undefined") return;
  const w = window as unknown as { __truweMissingI18n?: Set<string> };
  w.__truweMissingI18n = w.__truweMissingI18n ?? new Set<string>();
  if (w.__truweMissingI18n.has(key)) return;
  w.__truweMissingI18n.add(key);
  if (import.meta.env.DEV) {
    console.warn(`[i18n] no Hinglish string for "${key}" — showing English.`);
  }
}

export function LanguageProvider({
  initial,
  children,
}: {
  initial?: Lang;
  children: ReactNode;
}) {
  const [lang, setLangState] = useState<Lang>(
    HINGLISH_ENABLED ? (initial ?? DEFAULT_LANG) : DEFAULT_LANG,
  );

  // Cookie-blocked browsers: pick the localStorage choice up after hydration.
  // Runs once and only when it disagrees with the server-rendered language.
  // We also re-write the cookie from the saved choice so a cookie that was
  // cleared (or expired) is restored — that cookie is what makes the NEXT
  // server render come back in the right language instead of English.
  useEffect(() => {
    if (!HINGLISH_ENABLED) {
      // Hinglish is paused: clear any saved choice and put everyone on English.
      clearSavedLang();
      if (lang !== DEFAULT_LANG) setLangState(DEFAULT_LANG);
      return;
    }
    const saved = readSavedLang();
    if (saved) {
      persistLang(saved);
      if (saved !== lang) setLangState(saved);
    } else if (lang !== DEFAULT_LANG) {
      persistLang(lang);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep <html lang> honest for assistive tech. Hinglish is Roman-script
  // Hindi-English, so `hi-Latn-IN` is the accurate tag.
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.lang = lang === "hi-en" ? "hi-Latn-IN" : "en-IN";
    document.documentElement.dataset.lang = lang;
    pushContentLanguage(lang);
  }, [lang]);

  const setLang = useCallback(
    (next: Lang) => {
      // Side effects stay OUT of the state updater — React may invoke an
      // updater twice (StrictMode) and that would double-fire analytics.
      if (!HINGLISH_ENABLED) return;
      if (next === lang) return;
      persistLang(next);
      // One analytics event per deliberate switch. No PII, ever.
      trackLanguageToggle(lang, next);
      setLangState(next);
    },
    [lang],
  );

  // The Hinglish table is fetched on demand so the English-only default
  // build never carries it. Until it lands, English is shown (same fallback
  // as a missing key).
  const [hiTable, setHiTable] = useState<Partial<Record<TranslationKey, string>> | null>(null);
  useEffect(() => {
    if (lang !== "hi-en" || hiTable) return;
    let cancelled = false;
    void import("./dictionary.hi").then((m) => {
      if (!cancelled) setHiTable(m.hiEn);
    });
    return () => {
      cancelled = true;
    };
  }, [lang, hiTable]);

  const value = useMemo<LanguageContextValue>(() => {
    const table = lang === "hi-en" ? hiTable : null;
    return {
      lang,
      langLabel: LANG_LABELS[lang],
      setLang,
      has: (key) => (table ? typeof table[key] === "string" : true),
      t: (key, vars) => {
        const english = en[key];
        if (!table) return interpolate(english, vars);
        const translated = table[key];
        if (typeof translated === "string" && translated.length > 0) {
          return interpolate(translated, vars);
        }
        recordMissing(key);
        return interpolate(english, vars);
      },
    };
  }, [lang, hiTable, setLang]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

function useLanguageContext(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (ctx) return ctx;
  // Defensive default so a component rendered outside the provider (e.g. an
  // admin-only tree) still renders complete English instead of crashing.
  return {
    lang: DEFAULT_LANG,
    langLabel: LANG_LABELS[DEFAULT_LANG],
    setLang: () => {},
    has: () => true,
    t: (key, vars) => interpolate(en[key], vars),
  };
}

/** `const t = useT(); t("pdp.choosePack")` */
export function useT(): (key: TranslationKey, vars?: Vars) => string {
  return useLanguageContext().t;
}

/** Full language control: current value, label and setter. */
export function useLanguage(): LanguageContextValue {
  return useLanguageContext();
}

/**
 * Pick between two already-written strings (used for CMS-managed content with
 * paired English / Hinglish fields). Falls back to the English block whenever
 * the Hinglish field is empty — never a partial mix.
 */
export function pickLocalized(
  lang: Lang,
  english: string | null | undefined,
  hinglish: string | null | undefined,
): string {
  const fallback = english ?? "";
  if (lang !== "hi-en") return fallback;
  const value = (hinglish ?? "").trim();
  return value.length > 0 ? value : fallback;
}

/** Hook form of {@link pickLocalized} for CMS content blocks. */
export function useLocalizedField(): (
  english: string | null | undefined,
  hinglish: string | null | undefined,
) => string {
  const { lang } = useLanguageContext();
  return useCallback(
    (english, hinglish) => pickLocalized(lang, english, hinglish),
    [lang],
  );
}
